import { useId, useState, type FormEvent } from 'react';

import { isSessionConfigured, SessionError, signIn } from '../../api/session';
import { Owl } from '../components/Owl';
import { IconLock, IconMail } from '../components/Icons';

/**
 * Écran de connexion — Supabase Auth, e-mail et mot de passe.
 *
 * Il n'a pas de prop `onSignIn` : une connexion réussie modifie la session
 * Supabase, et c'est l'abonnement d'`App.tsx` qui bascule l'affichage. Faire
 * remonter l'information par un callback créerait une seconde source de
 * vérité, qui finirait par diverger de la vraie session au premier
 * renouvellement de jeton.
 *
 * Le compte est créé à la main dans le tableau de bord Supabase et
 * l'inscription publique y est désactivée : l'application reste
 * mono-utilisateur, et le serveur refuse de toute façon un second compte
 * (`OWNER_ALREADY_CLAIMED`). Il n'y a donc volontairement pas de formulaire
 * d'inscription — ni de lien qui en promettrait un.
 */
export function LoginScreen() {
  const emailId = useId();
  const passwordId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const configure = isSessionConfigured();

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (enCours) return;

    const adresse = email.trim();
    if (adresse === '') {
      setError('Renseigne une adresse e-mail pour continuer.');
      return;
    }
    if (password === '') {
      setError('Renseigne ton mot de passe pour continuer.');
      return;
    }

    setError(null);
    setEnCours(true);

    try {
      await signIn(adresse, password);
      // Rien de plus : `onSessionChange` dans App.tsx prend le relais.
    } catch (cause) {
      setError(
        cause instanceof SessionError ? cause.message : 'Connexion impossible pour le moment.',
      );
      // Le mot de passe est effacé, jamais l'adresse : c'est presque toujours
      // le mot de passe qui est en cause, et retaper l'e-mail à chaque essai
      // est une punition inutile.
      setPassword('');
      setEnCours(false);
    }
  }

  return (
    <div className="signin">
      <p className="signin-eyebrow">
        <Bean />
        Recherche de prospect
      </p>

      <Owl />

      <h1 className="signin-title">Hermès</h1>
      <p className="signin-lead">Connectez-vous pour reprendre votre prospection.</p>

      <form
        className="signin-card"
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        noValidate
      >
        <div className="field">
          <label className="sr-only" htmlFor={emailId}>
            Adresse e-mail
          </label>
          <div className="input-icon">
            <IconMail size={17} />
            <input
              id={emailId}
              className="input"
              type="email"
              name="email"
              autoComplete="username"
              placeholder="Adresse e-mail"
              value={email}
              disabled={enCours || !configure}
              aria-invalid={error !== null}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label className="sr-only" htmlFor={passwordId}>
            Mot de passe
          </label>
          <div className="input-icon">
            <IconLock size={17} />
            <input
              id={passwordId}
              className="input"
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Mot de passe"
              value={password}
              disabled={enCours || !configure}
              aria-invalid={error !== null}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
        </div>

        {error !== null ? (
          <p className="notice notice-error" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          className="btn btn-brand btn-block"
          disabled={enCours || !configure}
        >
          {enCours ? (
            <>
              <Bean spinning />
              Infusion en cours…
            </>
          ) : (
            'Se connecter'
          )}
        </button>
      </form>

      {configure ? null : (
        <p className="signin-note">
          Connexion indisponible : VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY ne sont
          pas renseignées dans le .env de la racine.
        </p>
      )}

      <p className="signin-foot">Compte unique — créé depuis le tableau de bord Supabase.</p>
    </div>
  );
}

/**
 * Le grain de café : puce de sur-titre, et témoin d'attente.
 *
 * Un grain qui tourne plutôt qu'un disque qui tourne — c'est la seule
 * concession de cet écran à sa propre thématique, et elle ne dure que le temps
 * de la requête.
 */
function Bean({ spinning = false }: { spinning?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: spinning ? 8 : 6,
        height: spinning ? 11 : 9,
        flex: 'none',
        borderRadius: '60% 60% 55% 55%',
        background: spinning ? 'var(--on-brand)' : 'var(--brand)',
        transform: spinning ? undefined : 'rotate(-18deg)',
        animation: spinning ? 'spin-bean 1.1s linear infinite' : undefined,
      }}
    />
  );
}
